import { useCallback, useEffect, useState } from 'react';
import { getSingleTransactionApi } from '../../../api/transaction';
import { InfoBlock } from '../../../components/InfoBlock';
import { InfoBlockItem } from '../../../components/InfoBLockItem';
import Loader from '../../../components/Loader';
import { tips } from '../../../constants/tips';
import { bigIntDecimalsDivision, bigIntFormat } from '../../../helpers/bignumber';
import { formatTransactionType } from '../../../helpers/formats';
import { formatTimestamp } from '../../../helpers/time';
import { useAppParams } from '../../../hooks/useAppParams';
import { useTransactionType } from '../../../hooks/useTransactionType';
import { ITransaction } from '../../../types/transaction';
import ResizebleCard from './ResizebleCard';
import styles from './index.module.scss';

export const TransactionInfoBlock = () => {
  const [data, setData] = useState<ITransaction | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const params = useAppParams();

  const { currencyTotal, currencyCode, transactionType } = useTransactionType({
    type: data?.type,
    subtype: data?.subtype,
    attachment: data?.attachment,
  });

  const getData = useCallback(() => {
    if (params.transaction) {
      setIsLoading(true);
      setData(null);
      if (params.transaction.length >= 64) {
        return getSingleTransactionApi({ fullHash: params.transaction })
          .then(setData)
          .finally(() => {
            setIsLoading(false);
          });
      }

      return getSingleTransactionApi({ transaction: params.transaction })
        .then(setData)
        .finally(() => {
          setIsLoading(false);
        });
    }
    setData(null);
  }, [params.transaction]);

  useEffect(() => {
    getData();
  }, [getData]);

  if (!params.transaction) return null;

  return (
    <div className='margin-bottom'>
      <InfoBlock title={`Transaction ${data?.transaction ?? ''} info`}>
        {isLoading && <Loader />}
        {!isLoading && data && (
          <>
            <InfoBlockItem
              name='Status'
              value={
                !data.errorMessage ? (
                  <span className={styles.transactionInfoStatus}>Success</span>
                ) : (
                  'Error'
                )
              }
              isSuccess
            />
            <InfoBlockItem
              name='Transaction ID'
              tooltip={tips.transaction}
              value={data.transaction}
            />
            <InfoBlockItem
              name='Transaction type'
              tooltip={tips.transactionType}
              value={formatTransactionType(transactionType || '')}
            />
            <InfoBlockItem
              name='Amount'
              tooltip={tips.amountAPL}
              value={
                transactionType === 'CurrencyTransfer'
                  ? `${currencyTotal ?? ''} ${currencyCode ?? ''}` ?? ''
                  : `${bigIntFormat(bigIntDecimalsDivision(data.amountATM, 8)) ?? ''} APL`
              }
            />
            <InfoBlockItem
              name='Fee'
              tooltip={tips.feeAPL}
              value={`${bigIntFormat(bigIntDecimalsDivision(data.feeATM, 8))} APL`}
            />
            <InfoBlockItem name='Sender Wallet' tooltip={tips.senderRS} value={data.senderRS} />
            <InfoBlockItem
              name='Recipient Wallet'
              tooltip={tips.recipientRS}
              value={data.recipientRS}
            />
            <InfoBlockItem
              name='Timestamp'
              tooltip={tips.timestamp}
              value={formatTimestamp(data.timestamp)}
            />
            <InfoBlockItem
              name='Timestamp (UTC)'
              tooltip={tips.timestampUTC}
              value={formatTimestamp(data.timestamp - 7200)}
            />
            <ResizebleCard data={data} />
          </>
        )}
      </InfoBlock>
    </div>
  );
};
